import { useState, useEffect, useCallback } from 'react';
import { money, money2 } from '../utils/currency';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Search, Download, FileText, CreditCard, CalendarDays, DollarSign, Ban, Trash2, Eye, Filter, Gift } from 'lucide-react';
import { format, subDays, addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import UpgradeGate from '../components/UpgradeGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_SUB_TABS = [
  { key: 'daily', labelKey: 'sales.dailySummary', icon: DollarSign, requireAll: true },
  { key: 'appointments', labelKey: 'sales.appointments', icon: CalendarDays, requireAll: true },
  { key: 'invoices', labelKey: 'sales.invoices', icon: FileText, requireAll: false },
  { key: 'payments', labelKey: 'sales.payments', icon: CreditCard, requireAll: true },
  { key: 'vouchers', labelKey: 'sales.giftCards', icon: Gift, requireGiftCards: true },
  { key: 'cancellations', labelKey: 'Cancellations', icon: Ban, requireAll: true },
];

export default function SalesPage() {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canViewAll = isAdmin || hasPermission('sales', 'view_all');
  const canVoid = isAdmin || hasPermission('sales', 'void_invoices');
  const canApplyDiscount = isAdmin || hasPermission('sales', 'apply_discounts');
  // Gift cards tab: separate permission from view_all so Basic staff can be given voucher-only access
  const canViewGiftCards = isAdmin || canViewAll || hasPermission('sales', 'view_gift_cards');

  const visibleTabs = ALL_SUB_TABS.filter(tab => {
    if (tab.requireGiftCards) return canViewGiftCards;
    if (tab.requireAll) return canViewAll;
    return true;
  });
  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.key || 'invoices');

  const contentArea = (
    <>
      {activeTab === 'daily' && <DailySalesSummary />}
      {activeTab === 'appointments' && <AppointmentsList />}
      {activeTab === 'invoices' && <InvoicesList canVoid={canVoid} />}
      {activeTab === 'payments' && <PaymentsSummary />}
      {activeTab === 'vouchers' && <UpgradeGate feature="gift_cards" featureName="Gift Cards"><VouchersList /></UpgradeGate>}
      {activeTab === 'cancellations' && <CancellationsList />}
    </>
  );

  return (
    <div className="fade-in" data-testid="sales-page">
      {/* Mobile: Horizontal pill tabs */}
      <div className="lg:hidden">
        <div className="flex items-center gap-2 overflow-x-auto pb-3 -mx-1 px-1 no-scrollbar">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                data-testid={`sales-tab-${tab.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${isActive ? 'bg-[#556B2F] text-white' : 'bg-[#F0F2EC] text-[#5C6656]'}`}>
                <Icon size={14} strokeWidth={1.5} />
                {tab.labelKey.includes('.') ? t(tab.labelKey) : tab.labelKey}
              </button>
            );
          })}
        </div>
        <div className="pb-20">{contentArea}</div>
      </div>

      {/* Desktop: Side nav layout */}
      <div className="hidden lg:flex h-[calc(100vh-6rem)]">
        <div className="w-48 flex-shrink-0 border-r border-[#E5E7E1] bg-white pr-2">
          <h2 className="text-lg font-semibold text-[#1A2421] px-3 pt-1 pb-3" style={{ fontFamily: 'Outfit' }}>{t('sales.title')}</h2>
          <nav className="space-y-0.5">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  data-testid={`sales-tab-${tab.key}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive ? 'bg-[#E9EDDF] text-[#556B2F]' : 'text-[#5C6656] hover:bg-[#F5F7F0]'}`}>
                  <Icon size={15} strokeWidth={1.5} />
                  {tab.labelKey.includes('.') ? t(tab.labelKey) : tab.labelKey}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="flex-1 overflow-auto px-6 py-2">{contentArea}</div>
      </div>
    </div>
  );
}

/* ========== DAILY SALES SUMMARY ========== */
function DailySalesSummary() {
  const { t } = useTranslation();
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const dateStr = format(date, 'yyyy-MM-dd');

  const fetch = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/sales/daily-summary?date=${dateStr}`, { withCredentials: true });
      setData(d);
    } catch (err) { console.error(err); }
  }, [dateStr]);

  useEffect(() => { fetch(); }, [fetch]);

  const ts = data?.transaction_summary || {};
  const totals = data?.totals || {};
  const cash = data?.cash_movement || {};
  const apptSummary = data?.appointment_summary || {};
  const staffBreakdown = data?.staff_breakdown || [];
  const serviceBreakdown = data?.service_breakdown || [];

  return (
    <div data-testid="daily-sales-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('sales.dailySales')}</h1>
          <p className="text-sm text-[#5C6656]">{t('sales.filterExport')}</p>
        </div>
        {data && (
          <Button variant="outline" className="border-[#556B2F] text-[#556B2F] text-sm" data-testid="sales-export-pdf"
            onClick={async () => {
              const rows = [];
              const ts = data.transaction_summary || {};
              const fmt = (n) => money2((Number(n) || 0));
              // Services
              if (ts.services) rows.push({ Type: 'Services', Qty: ts.services.qty || 0, Refunds: ts.services.refund_qty || 0, Gross: fmt(ts.services.gross_total) });
              if (ts.appointments) rows.push({ Type: 'Appointments', Qty: ts.appointments.qty || 0, Refunds: ts.appointments.refund_qty || 0, Gross: '—' });
              if (ts.tips) rows.push({ Type: 'Tips', Qty: '—', Refunds: '—', Gross: fmt(ts.tips.total) });
              if (ts.discounts) rows.push({ Type: 'Discounts', Qty: '—', Refunds: '—', Gross: `-${fmt(ts.discounts.total)}` });
              if (ts.voucher_redemptions) rows.push({ Type: 'Vouchers redeemed', Qty: ts.voucher_redemptions.qty || 0, Refunds: '—', Gross: `-${fmt(ts.voucher_redemptions.total)}` });
              if (ts.total_sales) rows.push({ Type: 'Total Sales', Qty: ts.total_sales.qty || 0, Refunds: 0, Gross: fmt(ts.total_sales.gross_total) });
              // Cash movement
              const cm = data.cash_movement || {};
              Object.entries(cm).forEach(([method, amt]) => {
                rows.push({ Type: `Cash Movement — ${method}`, Qty: '—', Refunds: '—', Gross: fmt(amt) });
              });
              // Staff
              const staff = data.staff_breakdown || [];
              staff.forEach(s => rows.push({ Type: `Staff — ${s.staff}`, Qty: s.qty || '—', Refunds: '—', Gross: fmt(s.revenue) }));
              // Services breakdown
              const svcb = data.service_breakdown || [];
              svcb.forEach(s => rows.push({ Type: `Service — ${s.service}`, Qty: s.qty || 0, Refunds: 0, Gross: fmt(s.revenue) }));
              try {
                const res = await axios.post(`${API}/reports/export-pdf`, {
                  title: `Daily Sales Summary - ${format(date, 'EEEE d MMM, yyyy')}`, rows, date_range: format(date, 'yyyy-MM-dd')
                }, { withCredentials: true, responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a'); a.href = url; a.download = `daily-sales-${format(date, 'yyyy-MM-dd')}.pdf`; a.click();
                URL.revokeObjectURL(url);
              } catch { toast.error('PDF generation failed'); }
            }}>
            <FileText size={14} className="mr-1" /> Export PDF
          </Button>
        )}
      </div>
      {/* Date picker */}
      <div className="flex items-center gap-2 mb-6">
        <Button size="sm" variant="ghost" onClick={() => setDate(d => subDays(d, 1))} className="h-8 w-8 p-0"><ChevronLeft size={18} /></Button>
        <button onClick={() => setDate(new Date())} className="px-3 py-1.5 text-sm font-medium text-[#556B2F] hover:bg-[#E9EDDF] rounded-md">Today</button>
        <input type="date" value={dateStr} onChange={e => { if (e.target.value) setDate(new Date(e.target.value + 'T12:00:00')); }} className="h-8 px-2 border border-[#E5E7E1] rounded-md text-sm text-[#1A2421] bg-white" style={{ fontSize: '16px' }} data-testid="sales-date-picker" />
        <span className="text-sm font-medium text-[#1A2421] hidden sm:inline">{format(date, 'EEEE d MMM, yyyy')}</span>
        <Button size="sm" variant="ghost" onClick={() => setDate(d => addDays(d, 1))} className="h-8 w-8 p-0"><ChevronRight size={18} /></Button>
      </div>

      {data && (
        <div className="space-y-6">
          {/* Appointment summary cards */}
          <div className="grid grid-cols-3 lg:grid-cols-7 gap-3" data-testid="appointment-summary">
            {[
              { label: 'Total', value: apptSummary.total || 0, color: '#1A2421' },
              { label: 'Confirmed', value: apptSummary.confirmed || 0, color: '#556B2F' },
              { label: 'Completed', value: apptSummary.completed || 0, color: '#3E6641' },
              { label: 'Cancelled', value: apptSummary.cancelled || 0, color: '#E2725B' },
              { label: 'No-show', value: apptSummary.no_show || 0, color: '#C0392B' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-[#E5E7E1] rounded-xl p-3 text-center">
                <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'Outfit' }}>{s.value}</p>
                <p className="text-[10px] text-[#5C6656] uppercase tracking-wider font-bold mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Voided indicator */}
          {(totals.invoices_voided || 0) > 0 && (
            <div className="bg-[#E2725B]/10 border border-[#E2725B]/20 rounded-lg px-4 py-2 flex items-center gap-2">
              <Ban size={14} className="text-[#E2725B]" />
              <span className="text-sm text-[#E2725B] font-medium">{totals.invoices_voided} invoice{totals.invoices_voided > 1 ? 's' : ''} voided (excluded from totals)</span>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Transaction summary */}
            <div className="border border-[#E5E7E1] rounded-xl p-5 bg-white">
              <h3 className="text-base font-semibold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>{t('sales.transactionSummary')}</h3>
              <table className="w-full text-sm" data-testid="transaction-summary-table">
                <thead><tr className="border-b border-[#E5E7E1]">
                  <th className="text-left py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Item type</th>
                  <th className="text-right py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Sales qty</th>
                  <th className="text-right py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Refund qty</th>
                  <th className="text-right py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Gross total</th>
                </tr></thead>
                <tbody>
                  {[
                    { label: 'Services', qty: ts.services?.qty || 0, refund: ts.services?.refund_qty || 0, total: ts.services?.gross_total || 0 },
                    { label: 'Appointments', qty: ts.appointments?.qty || 0, refund: ts.appointments?.refund_qty || 0, total: null },
                    { label: 'Tips', qty: '-', refund: '-', total: ts.tips?.total || 0 },
                    { label: 'Discounts', qty: '-', refund: '-', total: -(ts.discounts?.total || 0) },
                    { label: 'Vouchers redeemed', qty: ts.voucher_redemptions?.qty || 0, refund: '-', total: -(ts.voucher_redemptions?.total || 0), testid: 'vouchers-redeemed-row' },
                  ].map(row => (
                    <tr key={row.label} className="border-b border-[#E5E7E1]/50" data-testid={row.testid || undefined}><td className="py-2.5 text-[#1A2421]">{row.label}</td><td className="py-2.5 text-right text-[#5C6656]">{row.qty}</td><td className="py-2.5 text-right text-[#5C6656]">{row.refund}</td><td className="py-2.5 text-right font-medium text-[#1A2421]">{row.total === null ? '—' : `${'\u00A3'}${typeof row.total === 'number' ? row.total.toFixed(2) : row.total}`}</td></tr>
                  ))}
                  <tr className="font-bold border-t-2 border-[#E5E7E1]"><td className="py-3 text-[#1A2421]">Total Sales</td><td className="py-3 text-right">{ts.total_sales?.qty || 0}</td><td className="py-3 text-right">0</td><td className="py-3 text-right text-[#556B2F]">{'\u00A3'}{(ts.total_sales?.gross_total || 0).toFixed(2)}</td></tr>
                </tbody>
              </table>
            </div>
            {/* Cash movement summary */}
            <div className="border border-[#E5E7E1] rounded-xl p-5 bg-white">
              <h3 className="text-base font-semibold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>{t('sales.cashMovement')}</h3>
              <table className="w-full text-sm" data-testid="cash-movement-table">
                <thead><tr className="border-b border-[#E5E7E1]">
                  <th className="text-left py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Payment type</th>
                  <th className="text-right py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Payments collected</th>
                  <th className="text-right py-2 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Refunds paid</th>
                </tr></thead>
                <tbody>
                  {['card', 'cash', 'bank_transfer', 'voucher', 'other'].map(pm => (
                    <tr key={pm} className="border-b border-[#E5E7E1]/50"><td className="py-2.5 text-[#1A2421] capitalize">{pm === 'bank_transfer' ? 'Bank Transfer' : pm}</td><td className="py-2.5 text-right text-[#5C6656]">{'\u00A3'}{(cash[pm] || 0).toFixed(2)}</td><td className="py-2.5 text-right text-[#5C6656]">{'\u00A3'}0.00</td></tr>
                  ))}
                  <tr className="font-bold border-t-2 border-[#E5E7E1]"><td className="py-3 text-[#1A2421]">Payments collected</td><td className="py-3 text-right text-[#556B2F]">{'\u00A3'}{(totals.gross_revenue || 0).toFixed(2)}</td><td className="py-3 text-right">{'\u00A3'}0.00</td></tr>
                  <tr className="font-bold"><td className="py-2 text-[#1A2421]">Of which tips</td><td className="py-2 text-right text-[#556B2F]">{'\u00A3'}{(totals.tips || 0).toFixed(2)}</td><td className="py-2 text-right">{'\u00A3'}0.00</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Staff breakdown */}
          {staffBreakdown.length > 0 && (
            <div className="border border-[#E5E7E1] rounded-xl p-5 bg-white" data-testid="staff-performance-section">
              <h3 className="text-base font-semibold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>Staff Performance</h3>
              <div className="space-y-3">
                {staffBreakdown.map((s, i) => {
                  const maxRev = staffBreakdown[0]?.revenue || 1;
                  const pct = Math.round((s.revenue / maxRev) * 100);
                  return (
                    <div key={i} className="flex items-center gap-3" data-testid={`staff-perf-${i}`}>
                      <div className="w-7 h-7 rounded-full bg-[#E9EDDF] flex items-center justify-center text-[10px] font-bold text-[#556B2F] flex-shrink-0">{s.staff?.charAt(0)?.toUpperCase()}</div>
                      <span className="text-sm text-[#1A2421] w-24 truncate flex-shrink-0 font-medium">{s.staff}</span>
                      <div className="flex-1 h-7 bg-[#F0F2EC] rounded-lg overflow-hidden">
                        <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: i === 0 ? '#556B2F' : '#8B9A6B' }} />
                      </div>
                      <span className="text-sm font-bold text-[#1A2421] w-20 text-right flex-shrink-0">{'\u00A3'}{s.revenue.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Service summary */}
          {serviceBreakdown.length > 0 && (
            <div className="border border-[#E5E7E1] rounded-xl p-5 bg-white" data-testid="service-summary-section">
              <h3 className="text-base font-semibold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>Service Summary</h3>
              <div className="space-y-3">
                {serviceBreakdown.map((s, i) => {
                  const maxRev = serviceBreakdown[0]?.revenue || 1;
                  const pct = Math.round((s.revenue / maxRev) * 100);
                  return (
                    <div key={i} data-testid={`service-perf-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-[#1A2421] truncate mr-3">{s.service}</span>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-[#5C6656] bg-[#F0F2EC] rounded-full px-2 py-0.5">{s.qty}x</span>
                          <span className="text-sm font-bold text-[#1A2421] w-20 text-right">{'\u00A3'}{s.revenue.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="h-5 bg-[#F0F2EC] rounded-lg overflow-hidden">
                        <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: i === 0 ? '#556B2F' : i < 3 ? '#8B9A6B' : '#B8C4A0' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Totals row */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t-2 border-[#E5E7E1]">
                <span className="text-sm font-bold text-[#1A2421]">Total</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#5C6656] bg-[#F0F2EC] rounded-full px-2 py-0.5">{serviceBreakdown.reduce((sum, s) => sum + s.qty, 0)}x</span>
                  <span className="text-sm font-bold text-[#556B2F] w-20 text-right">{'\u00A3'}{serviceBreakdown.reduce((sum, s) => sum + s.revenue, 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ========== APPOINTMENTS LIST ========== */
function AppointmentsList() {
  const { t } = useTranslation();
  const [appts, setAppts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [selectedApptDetail, setSelectedApptDetail] = useState(null);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  const [exporting, setExporting] = useState(false);
  const limit = 20;

  useEffect(() => {
    axios.get(`${API}/staff`, { withCredentials: true }).then(r => setStaffList(r.data || [])).catch(() => {});
  }, []);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (staffFilter) params.set('staff_id', staffFilter);
    return params.toString();
  }, [search, dateFrom, dateTo, statusFilter, staffFilter]);

  const fetchAppts = useCallback(async () => {
    try {
      const qs = buildQuery();
      const { data } = await axios.get(`${API}/sales/appointments?${qs}&page=${page}&limit=${limit}`, { withCredentials: true });
      setAppts(data.appointments);
      setTotal(data.total);
    } catch (err) { console.error(err); }
  }, [buildQuery, page]);

  useEffect(() => { fetchAppts(); }, [fetchAppts]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const qs = buildQuery();
      const resp = await axios.get(`${API}/sales/appointments/export?${qs}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appointments-export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Appointments exported');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const viewApptInvoice = async (appt) => {
    try {
      const { data } = await axios.get(`${API}/invoices/by-appointment/${appt.id}`, { withCredentials: true });
      if (data.found) setSelectedApptDetail(data.invoice);
      else setSelectedApptDetail({ ...appt, invoice_number: 'N/A', notice: 'No invoice yet — appointment not checked out.' });
    } catch { setSelectedApptDetail({ ...appt, invoice_number: 'N/A', notice: 'Could not load invoice.' }); }
  };

  const viewClientDetail = async (appt) => {
    if (!appt.client_id || appt.client_id === '__walk_in__') {
      setSelectedClientDetail({ name: appt.client_name || 'Walk-in', email: '', phone: '', isWalkIn: true });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/clients?search=${encodeURIComponent(appt.client_name)}&limit=1`, { withCredentials: true });
      const clients = data.clients || data;
      setSelectedClientDetail(clients.length > 0 ? clients[0] : { name: appt.client_name, email: '', phone: '' });
    } catch { setSelectedClientDetail({ name: appt.client_name, email: '', phone: '' }); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="appointments-list-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Appointments</h1>
          <p className="text-sm text-[#5C6656]">View, filter and export appointments.</p>
        </div>
        <Button onClick={handleExport} disabled={exporting} variant="outline" className="border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/5" data-testid="export-appointments-btn">
          <Download size={14} className="mr-2" />{exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6656]" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by Ref or Client" className="pl-9 border-[#E5E7E1]" data-testid="appt-search" />
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="border-[#E5E7E1] w-[145px] text-sm" data-testid="filter-date-from" />
          <span className="text-xs text-[#5C6656]">to</span>
          <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="border-[#E5E7E1] w-[145px] text-sm" data-testid="filter-date-to" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="h-9 px-3 rounded-md border border-[#E5E7E1] text-sm text-[#1A2421] bg-white" data-testid="filter-status">
          <option value="all">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="no_show">No show</option>
        </select>
        <select value={staffFilter} onChange={e => { setStaffFilter(e.target.value); setPage(1); }} className="h-9 px-3 rounded-md border border-[#E5E7E1] text-sm text-[#1A2421] bg-white" data-testid="filter-staff">
          <option value="">All staff</option>
          {staffList.map(s => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
        </select>
        {(dateFrom || dateTo || statusFilter !== 'all' || staffFilter) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter('all'); setStaffFilter(''); setPage(1); }} className="text-xs text-[#E2725B] hover:underline" data-testid="clear-filters">Clear filters</button>
        )}
      </div>
      {/* Table */}
      <div className="border border-[#E5E7E1] rounded-xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="appointments-table">
            <thead><tr className="border-b border-[#E5E7E1] bg-[#FAFAF8]">
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Ref #</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Client</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Service</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Created by</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Scheduled Date</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Duration</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Team member</th>
              <th className="text-right px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Price</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Status</th>
            </tr></thead>
            <tbody>
              {appts.map(a => (
                <tr key={a.id} className="border-b border-[#E5E7E1]/50 hover:bg-[#F9F8F6] transition-colors" data-testid={`appt-row-${a.id}`}>
                  <td className="px-4 py-3"><button onClick={() => viewApptInvoice(a)} className="text-[#556B2F] font-mono text-xs hover:underline cursor-pointer" data-testid={`ref-link-${a.id}`}>#{a.ref}</button></td>
                  <td className="px-4 py-3"><button onClick={() => viewClientDetail(a)} className="text-[#556B2F] font-medium hover:underline cursor-pointer" data-testid={`client-link-${a.id}`}>{a.client_name}</button></td>
                  <td className="px-4 py-3 text-[#1A2421]">{a.service_name}</td>
                  <td className="px-4 py-3 text-[#5C6656]">{a.created_by}</td>
                  <td className="px-4 py-3 text-[#1A2421]">{a.date}, {a.start_time}</td>
                  <td className="px-4 py-3 text-[#5C6656]">{a.duration}min</td>
                  <td className="px-4 py-3 text-[#1A2421]">{a.staff_name}</td>
                  <td className="px-4 py-3 text-right font-medium text-[#1A2421]">{'\u00A3'}{(a.price || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-[10px] capitalize ${a.status === 'confirmed' ? 'bg-[#556B2F] text-white' : a.status === 'completed' ? 'bg-[#3E6641] text-white' : a.status === 'cancelled' ? 'bg-[#E2725B] text-white' : 'bg-[#D4A373] text-white'}`}>
                      {a.status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {appts.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#5C6656]">No appointments found</td></tr>}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7E1] bg-[#FAFAF8]">
            <span className="text-xs text-[#5C6656]">{total} appointments</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs"><ChevronLeft size={14} /></Button>
              <span className="text-xs text-[#5C6656] px-2">Page {page} of {totalPages}</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs"><ChevronRight size={14} /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice/Receipt Detail Dialog */}
      <Dialog open={!!selectedApptDetail} onOpenChange={() => setSelectedApptDetail(null)}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1]">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}><FileText size={18} className="inline mr-2 text-[#556B2F]" />Invoice {selectedApptDetail?.invoice_number || ''}</DialogTitle></DialogHeader>
          {selectedApptDetail && (
            <div className="space-y-3 mt-2">
              {selectedApptDetail.notice ? (
                <p className="text-sm text-[#5C6656] py-4 text-center">{selectedApptDetail.notice}</p>
              ) : (
                <>
                  <div className={`px-3 py-1.5 rounded-lg inline-block text-xs font-semibold capitalize ${selectedApptDetail.status === 'paid' ? 'bg-[#3E6641] text-white' : selectedApptDetail.status === 'voided' ? 'bg-[#E2725B] text-white' : 'bg-[#D4A373] text-white'}`}>{selectedApptDetail.status}</div>
                  <div className="border border-[#E5E7E1] rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[#5C6656]">Client</span><span className="font-medium">{selectedApptDetail.client_name}</span></div>
                    <div className="flex justify-between"><span className="text-[#5C6656]">Service</span><span className="font-medium">{selectedApptDetail.service_name}</span></div>
                    <div className="flex justify-between"><span className="text-[#5C6656]">Staff</span><span className="font-medium">{selectedApptDetail.staff_name}</span></div>
                    <div className="flex justify-between"><span className="text-[#5C6656]">Date</span><span className="font-medium">{selectedApptDetail.date}</span></div>
                    <div className="flex justify-between"><span className="text-[#5C6656]">Payment</span><span className="font-medium capitalize">{selectedApptDetail.payment_method}</span></div>
                  </div>
                  <div className="border border-[#E5E7E1] rounded-xl p-4 text-sm">
                    <div className="flex justify-between"><span className="text-[#5C6656]">Subtotal</span><span>{'\u00A3'}{(selectedApptDetail.price || 0).toFixed(2)}</span></div>
                    {selectedApptDetail.discount > 0 && <div className="flex justify-between text-[#E2725B]"><span>Discount</span><span>-{'\u00A3'}{selectedApptDetail.discount?.toFixed(2)}</span></div>}
                    {selectedApptDetail.tip > 0 && <div className="flex justify-between text-[#3E6641]"><span>Tip</span><span>+{'\u00A3'}{selectedApptDetail.tip?.toFixed(2)}</span></div>}
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-[#E5E7E1] mt-1"><span>Total</span><span className="text-[#556B2F]">{'\u00A3'}{(selectedApptDetail.final_total || 0).toFixed(2)}</span></div>
                  </div>
                  <Button variant="outline" className="w-full border-[#556B2F] text-[#556B2F]" data-testid="download-invoice-pdf"
                    onClick={() => window.open(`${API}/invoices/${selectedApptDetail.id}/pdf`, '_blank')}>
                    <Download size={14} className="mr-2" /> Download Invoice PDF
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClientDetail} onOpenChange={() => setSelectedClientDetail(null)}>
        <DialogContent className="sm:max-w-sm border-[#E5E7E1]">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Client Details</DialogTitle></DialogHeader>
          {selectedClientDetail && (
            <div className="mt-2 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-[#E9EDDF] flex items-center justify-center text-xl font-bold text-[#556B2F]">{selectedClientDetail.name?.charAt(0)?.toUpperCase()}</div>
                <div>
                  <p className="font-semibold text-lg text-[#1A2421]">{selectedClientDetail.name}</p>
                  {selectedClientDetail.isWalkIn && <Badge className="bg-[#D4A373] text-white text-xs">Walk-in</Badge>}
                </div>
              </div>
              {selectedClientDetail.email && <p className="text-sm text-[#5C6656]">{selectedClientDetail.email}</p>}
              {selectedClientDetail.phone && <p className="text-sm text-[#5C6656]">{selectedClientDetail.phone}</p>}
              {selectedClientDetail.created_at && <p className="text-xs text-[#5C6656]">Client since: {new Date(selectedClientDetail.created_at).toLocaleDateString()}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========== INVOICES LIST ========== */
function InvoicesList({ canVoid = true }) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [vatInfo, setVatInfo] = useState({ vat_enabled: false, vat_rate: 20, vat_number: '' });
  const limit = 20;

  const fetchInvoices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sales/invoices?search=${encodeURIComponent(search)}&date=${encodeURIComponent(date)}&page=${page}&limit=${limit}`, { withCredentials: true });
      setInvoices(data.invoices);
      setTotal(data.total);
      if (data.vat) setVatInfo(data.vat);
    } catch (err) { console.error(err); }
  }, [search, page, date]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const shiftDay = (n) => {
    const d = new Date(date + 'T00:00:00');
    const shifted = n < 0 ? subDays(d, Math.abs(n)) : addDays(d, n);
    setDate(format(shifted, 'yyyy-MM-dd'));
    setPage(1);
  };

  const isToday = date === format(new Date(), 'yyyy-MM-dd');
  const dayTotal = invoices.reduce((sum, inv) => sum + (inv.final_total || 0), 0);

  const voidInvoice = async (inv) => {
    if (!window.confirm(`Void invoice ${inv.invoice_number}? This will revert the appointment to confirmed.`)) return;
    try {
      await axios.put(`${API}/sales/invoices/${inv.id}/void`, {}, { withCredentials: true });
      toast.success(`Invoice ${inv.invoice_number} voided`);
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to void'); }
  };

  const archiveInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/sales/invoices/${inv.id}`, { withCredentials: true });
      toast.success(`Invoice ${inv.invoice_number} deleted`);
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="invoices-list-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('nav.sales', 'Sales')}</h1>
          <p className="text-sm text-[#5C6656]">{isToday ? t('sales.today') : format(new Date(date + 'T00:00:00'), 'EEEE, d MMM yyyy')} · {t('sales.dayInvoiceCount', { count: total })} · {money2(dayTotal)}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1 border border-[#E5E7E1] rounded-lg bg-white">
          <Button variant="ghost" size="sm" onClick={() => shiftDay(-1)} className="h-9 w-9 p-0 text-[#556B2F] hover:bg-[#F5F7F0]" data-testid="sales-date-prev"><ChevronLeft size={16} /></Button>
          <input type="date" value={date} onChange={e => { setDate(e.target.value); setPage(1); }} className="h-9 px-2 text-sm bg-transparent border-0 focus:outline-none text-[#1A2421]" data-testid="sales-date-picker" />
          <Button variant="ghost" size="sm" onClick={() => shiftDay(1)} className="h-9 w-9 p-0 text-[#556B2F] hover:bg-[#F5F7F0]" data-testid="sales-date-next"><ChevronRight size={16} /></Button>
        </div>
        {!isToday && (
          <Button variant="outline" size="sm" onClick={() => { setDate(format(new Date(), 'yyyy-MM-dd')); setPage(1); }} className="h-9 border-[#556B2F] text-[#556B2F] text-xs" data-testid="sales-date-today">{t('sales.today')}</Button>
        )}
        <div className="relative flex-1 max-w-sm min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6656]" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder={t('sales.searchInvoice')} className="pl-9 border-[#E5E7E1]" data-testid="invoice-search" />
        </div>
      </div>
      <div className="border border-[#E5E7E1] rounded-xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead><tr className="border-b border-[#E5E7E1] bg-[#FAFAF8]">
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Invoice #</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Client</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Service</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Staff</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Date</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Payment</th>
              <th className="text-right px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Total</th>
              <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Status</th>
              <th className="text-right px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Actions</th>
            </tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b border-[#E5E7E1]/50 hover:bg-[#F9F8F6] transition-colors" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 text-[#556B2F] font-mono text-xs font-semibold">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-[#556B2F] font-medium">{inv.client_name}</td>
                  <td className="px-4 py-3 text-[#1A2421]">{inv.service_name}</td>
                  <td className="px-4 py-3 text-[#5C6656]">{inv.staff_name}</td>
                  <td className="px-4 py-3 text-[#1A2421]">{inv.date}</td>
                  <td className="px-4 py-3"><span className="capitalize text-xs px-2 py-1 rounded bg-[#E9EDDF] text-[#556B2F] font-medium">{inv.payment_method === 'bank_transfer' ? 'Bank' : inv.payment_method}</span></td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1A2421]">{'\u00A3'}{(inv.final_total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-[10px] capitalize ${inv.status === 'paid' ? 'bg-[#3E6641] text-white' : inv.status === 'voided' ? 'bg-[#E2725B] text-white' : 'bg-[#D4A373] text-white'}`}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDetailInvoice(inv)} className="h-7 w-7 p-0 text-[#556B2F]" data-testid={`view-invoice-${inv.id}`}><Eye size={14} /></Button>
                      {canVoid && inv.status === 'paid' && <Button size="sm" variant="ghost" onClick={() => voidInvoice(inv)} className="h-7 w-7 p-0 text-[#D4A373]" data-testid={`void-invoice-${inv.id}`}><Ban size={14} /></Button>}
                      {canVoid && <Button size="sm" variant="ghost" onClick={() => archiveInvoice(inv)} className="h-7 w-7 p-0 text-[#E2725B]" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={14} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#5C6656]">{t('sales.noInvoicesOnDay')}</td></tr>}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7E1] bg-[#FAFAF8]">
            <span className="text-xs text-[#5C6656]">{total} invoices</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs"><ChevronLeft size={14} /></Button>
              <span className="text-xs text-[#5C6656] px-2">Page {page} of {totalPages}</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs"><ChevronRight size={14} /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!detailInvoice} onOpenChange={() => setDetailInvoice(null)}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <FileText size={18} className="text-[#556B2F]" />
              Invoice {detailInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {detailInvoice && (
            <div className="space-y-4 mt-2">
              <div className={`px-3 py-1.5 rounded-lg inline-block text-xs font-semibold capitalize ${detailInvoice.status === 'paid' ? 'bg-[#3E6641] text-white' : detailInvoice.status === 'voided' ? 'bg-[#E2725B] text-white' : 'bg-[#D4A373] text-white'}`}>
                {detailInvoice.status}
              </div>
              <div className="border border-[#E5E7E1] rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#5C6656]">Client</span><span className="font-medium text-[#1A2421]">{detailInvoice.client_name}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Service</span><span className="font-medium text-[#1A2421]">{detailInvoice.service_name}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Staff</span><span className="font-medium text-[#1A2421]">{detailInvoice.staff_name}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Date</span><span className="font-medium text-[#1A2421]">{detailInvoice.date}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Time</span><span className="font-medium text-[#1A2421]">{detailInvoice.start_time} - {detailInvoice.end_time}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Duration</span><span className="font-medium text-[#1A2421]">{detailInvoice.duration}min</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Payment</span><span className="font-medium text-[#1A2421] capitalize">{detailInvoice.payment_method}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6656]">Created by</span><span className="font-medium text-[#1A2421]">{detailInvoice.created_by}</span></div>
              </div>
              <div className="border border-[#E5E7E1] rounded-xl p-4 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-[#5C6656]">Subtotal</span><span>{'\u00A3'}{(detailInvoice.price || 0).toFixed(2)}</span></div>
                {detailInvoice.discount > 0 && <div className="flex justify-between text-[#E2725B]"><span>Discount</span><span>-{'\u00A3'}{detailInvoice.discount.toFixed(2)}</span></div>}
                {detailInvoice.tip > 0 && <div className="flex justify-between text-[#3E6641]"><span>Tip</span><span>+{'\u00A3'}{detailInvoice.tip.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-[#E5E7E1]"><span>Total</span><span className="text-[#556B2F]">{'\u00A3'}{(detailInvoice.final_total || 0).toFixed(2)}</span></div>
                {vatInfo.vat_enabled && vatInfo.vat_rate > 0 && (detailInvoice.final_total || 0) > 0 && (() => {
                  const net = (detailInvoice.final_total || 0) / (1 + vatInfo.vat_rate / 100);
                  const vat = (detailInvoice.final_total || 0) - net;
                  return (
                    <div className="mt-2 pt-2 border-t border-[#E5E7E1] text-xs text-[#5C6656] space-y-0.5">
                      <div className="flex justify-between"><span>Net (excl. VAT)</span><span>{'\u00A3'}{net.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>VAT ({vatInfo.vat_rate}%)</span><span>{'\u00A3'}{vat.toFixed(2)}</span></div>
                      {vatInfo.vat_number && <div className="flex justify-between text-[10px]"><span>VAT No</span><span>{vatInfo.vat_number}</span></div>}
                    </div>
                  );
                })()}
              </div>
              {detailInvoice.voided_at && <p className="text-xs text-[#E2725B]">Voided at: {new Date(detailInvoice.voided_at).toLocaleString()}{detailInvoice.voided_by ? ` by ${detailInvoice.voided_by}` : ''}</p>}
              <Button variant="outline" className="w-full border-[#556B2F] text-[#556B2F]" data-testid="detail-download-pdf"
                onClick={() => window.open(`${API}/invoices/${detailInvoice.id}/pdf`, '_blank')}>
                <Download size={14} className="mr-2" /> Download Invoice PDF
              </Button>
              <div className="flex gap-2 pt-2">
                {detailInvoice.status === 'paid' && <Button onClick={() => { voidInvoice(detailInvoice); setDetailInvoice(null); }} variant="outline" className="flex-1 border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white"><Ban size={14} className="mr-1" /> Void</Button>}
                <Button onClick={() => { archiveInvoice(detailInvoice); setDetailInvoice(null); }} variant="outline" className="flex-1 border-[#E2725B] text-[#E2725B] hover:bg-[#E2725B] hover:text-white"><Trash2 size={14} className="mr-1" /> Delete</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========== PAYMENTS SUMMARY ========== */
function PaymentsSummary() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sales/payments`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(err => console.error(err));
  }, []);

  const byMethod = data?.by_method || {};

  return (
    <div data-testid="payments-view">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('sales.payments')}</h1>
        <p className="text-sm text-[#5C6656]">{t('sales.paymentSplit')}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="border border-[#E5E7E1] rounded-xl p-4 bg-white">
          <p className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('sales.totalCollected')}</p>
          <p className="text-2xl font-bold text-[#556B2F] mt-1">{'\u00A3'}{(data?.total_collected || 0).toFixed(2)}</p>
        </div>
        <div className="border border-[#E5E7E1] rounded-xl p-4 bg-white">
          <p className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('sales.totalTips')}</p>
          <p className="text-2xl font-bold text-[#3E6641] mt-1">{'\u00A3'}{(data?.total_tips || 0).toFixed(2)}</p>
        </div>
        <div className="border border-[#E5E7E1] rounded-xl p-4 bg-white">
          <p className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">Invoices</p>
          <p className="text-2xl font-bold text-[#1A2421] mt-1">{data?.invoice_count || 0}</p>
        </div>
      </div>

      {/* By method table */}
      <div className="border border-[#E5E7E1] rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead><tr className="border-b border-[#E5E7E1] bg-[#FAFAF8]">
            <th className="text-left px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('sales.paymentMethod')}</th>
            <th className="text-right px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('sales.transactions')}</th>
            <th className="text-right px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">Total</th>
            <th className="text-right px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#5C6656]">{t('sales.tips')}</th>
          </tr></thead>
          <tbody>
            {Object.entries(byMethod).map(([pm, info]) => (
              <tr key={pm} className="border-b border-[#E5E7E1]/50 hover:bg-[#F9F8F6]">
                <td className="px-4 py-3 font-medium text-[#1A2421] capitalize">{pm === 'bank_transfer' ? 'Bank Transfer' : pm}</td>
                <td className="px-4 py-3 text-right text-[#5C6656]">{info.count}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#1A2421]">{'\u00A3'}{info.total.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-[#3E6641]">{'\u00A3'}{info.tips.toFixed(2)}</td>
              </tr>
            ))}
            {Object.keys(byMethod).length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5C6656]">{t('sales.noPayments')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ========== VOUCHERS / GIFT CARDS LIST ========== */
function VouchersList() {
  const { t } = useTranslation();
  const [vouchers, setVouchers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const fetchVouchers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vouchers?search=${encodeURIComponent(search)}`, { withCredentials: true });
      setVouchers(data);
    } catch { toast.error('Failed to load vouchers'); }
  }, [search]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const statusColors = { valid: 'text-[#3E6641]', expired: 'text-[#E2725B]', redeemed: 'text-[#5C6656]', voided: 'text-[#C0392B]', refunded: 'text-[#D4A373]' };

  if (selectedVoucher) {
    const v = selectedVoucher;
    const statusLabel = v.status === 'valid' ? 'Valid' : v.status === 'expired' ? 'Expired' : v.status === 'redeemed' ? 'Redeemed' : v.status === 'voided' ? 'Voided' : v.status === 'refunded' ? 'Refunded' : v.status;
    const statusBg = v.status === 'valid' ? 'bg-[#3E6641]' : v.status === 'voided' ? 'bg-[#C0392B]' : v.status === 'refunded' ? 'bg-[#D4A373]' : v.status === 'expired' ? 'bg-[#E2725B]' : 'bg-[#5C6656]';
    return (
      <div className="space-y-4" data-testid="voucher-detail">
        <button onClick={() => setSelectedVoucher(null)} className="text-sm text-[#556B2F] hover:underline flex items-center gap-1"><ChevronLeft size={14} /> Back to Gift Cards</button>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('sales.giftCard')}</h2>
          <Badge className={`text-xs ${statusBg} text-white`}>{statusLabel}</Badge>
        </div>
        <div className={`rounded-2xl p-6 text-white ${v.status === 'voided' || v.status === 'refunded' ? 'bg-gradient-to-br from-[#5C6656] to-[#3A3A3A] opacity-70' : 'bg-gradient-to-br from-[#556B2F] to-[#3E6641]'}`} data-testid="voucher-card">
          <p className="text-3xl font-bold">{'\u00A3'}{(v.value || 0).toFixed(2)}</p>
          {(v.status === 'voided' || v.status === 'refunded') && <p className="text-sm opacity-70 mt-1 uppercase">{v.status}</p>}
          <div className="flex justify-between mt-4 text-sm opacity-90">
            <div><p className="text-[10px] uppercase tracking-wider opacity-70">Code</p><p className="font-mono font-bold">{v.code}</p></div>
            <div className="text-right"><p className="text-[10px] uppercase tracking-wider opacity-70">Expires</p><p className="font-medium">{v.expiry_date}</p></div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-3 bg-[#F9F8F6] rounded-lg"><p className="text-[10px] uppercase text-[#5C6656] font-bold">Total</p><p className="text-lg font-bold text-[#1A2421]">{'\u00A3'}{(v.value||0).toFixed(2)}</p></div>
          <div className="p-3 bg-[#F9F8F6] rounded-lg"><p className="text-[10px] uppercase text-[#5C6656] font-bold">Redeemed</p><p className="text-lg font-bold text-[#E2725B]">{'\u00A3'}{(v.redeemed_amount||0).toFixed(2)}</p></div>
          <div className="p-3 bg-[#F9F8F6] rounded-lg"><p className="text-[10px] uppercase text-[#5C6656] font-bold">Remaining</p><p className="text-lg font-bold text-[#3E6641]">{'\u00A3'}{(v.remaining||0).toFixed(2)}</p></div>
        </div>

        {/* Action buttons - Void / Refund */}
        {v.status === 'valid' && (
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 border-[#E2725B] text-[#E2725B] hover:bg-[#E2725B] hover:text-white text-sm"
              data-testid="void-voucher-btn"
              onClick={async () => {
                if (!window.confirm(`Void gift card ${v.code}? This makes it permanently unusable. No refund will be issued.`)) return;
                try {
                  await axios.put(`${API}/vouchers/${v.id}/void`, {}, { withCredentials: true });
                  toast.success('Gift card voided');
                  setSelectedVoucher({ ...v, status: 'voided', remaining: 0 });
                  fetchVouchers();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to void'); }
              }}>
              <Ban size={14} className="mr-1.5" /> Void
            </Button>
            <Button variant="outline" className="flex-1 border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white text-sm"
              data-testid="refund-voucher-btn"
              onClick={async () => {
                if (!window.confirm(`Refund gift card ${v.code} (${money2((v.remaining || v.value || 0))})? The gift card will be cancelled and the amount should be returned to the client.`)) return;
                try {
                  const { data } = await axios.put(`${API}/vouchers/${v.id}/refund`, {}, { withCredentials: true });
                  toast.success(data.message || 'Gift card refunded');
                  setSelectedVoucher({ ...v, status: 'refunded', remaining: 0 });
                  fetchVouchers();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to refund'); }
              }}>
              <CreditCard size={14} className="mr-1.5" /> Refund
            </Button>
          </div>
        )}
        {v.status === 'voided' && v.voided_at && (
          <p className="text-xs text-[#C0392B] text-center">Voided on {new Date(v.voided_at).toLocaleDateString()} by {v.voided_by || 'staff'}</p>
        )}
        {v.status === 'refunded' && v.refunded_at && (
          <p className="text-xs text-[#D4A373] text-center">Refunded {money2((v.refund_amount || 0))} on {new Date(v.refunded_at).toLocaleDateString()} by {v.refunded_by || 'staff'}</p>
        )}

        {/* Delete button - always available */}
        <div className="pt-2 border-t border-[#E5E7E1]">
          <Button variant="outline" className="w-full border-[#E2725B]/30 text-[#E2725B] hover:bg-[#E2725B] hover:text-white text-sm"
            data-testid="delete-voucher-btn"
            onClick={async () => {
              if (!window.confirm(`Permanently delete gift card ${v.code}? This cannot be undone.`)) return;
              try {
                await axios.delete(`${API}/vouchers/${v.id}`, { withCredentials: true });
                toast.success('Gift card deleted');
                setSelectedVoucher(null);
                fetchVouchers();
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
            }}>
            <Trash2 size={14} className="mr-1.5" /> Delete Gift Card
          </Button>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b border-[#E5E7E1]"><span className="text-[#5C6656]">Sale #</span><span className="text-[#1A2421] font-medium">{v.sale_number}</span></div>
          <div className="flex justify-between py-2 border-b border-[#E5E7E1]"><span className="text-[#5C6656]">Purchaser</span><span className="text-[#1A2421] font-medium">{v.purchaser_name || 'Walk-in'}</span></div>
          <div className="flex justify-between py-2 border-b border-[#E5E7E1]"><span className="text-[#5C6656]">Owner</span><span className="text-[#1A2421] font-medium">{v.owner_name || 'Not claimed'}</span></div>
          <div className="flex justify-between py-2 border-b border-[#E5E7E1]"><span className="text-[#5C6656]">Payment</span><span className="text-[#1A2421] font-medium capitalize">{v.payment_method}</span></div>
          <div className="flex justify-between py-2 border-b border-[#E5E7E1]"><span className="text-[#5C6656]">Sold by</span><span className="text-[#1A2421] font-medium">{v.sold_by}</span></div>
        </div>
        {v.redemptions?.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-[#1A2421] mb-2">{t('sales.redemptionHistory')}</h3>
            {v.redemptions.map((r, i) => (
              <div key={i} className="flex justify-between py-2 border-b border-[#E5E7E1] text-sm">
                <span className="text-[#5C6656]">{new Date(r.date).toLocaleDateString()} by {r.redeemed_by}</span>
                <span className="text-[#E2725B] font-medium">-{'\u00A3'}{r.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vouchers-list">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('sales.giftCards')}</h2>
          <p className="text-sm text-[#5C6656] mt-0.5">{t('sales.giftCardDesc')}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6656]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('sales.searchVoucher')} className="pl-9 border-[#E5E7E1] h-9 text-sm" data-testid="voucher-search" />
        </div>
      </div>
      <div className="border border-[#E5E7E1] rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vouchers-table">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E7E1]">
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[#5C6656]">
                <th className="px-4 py-3">Gift card</th>
                <th className="px-3 py-3">Issue date</th>
                <th className="px-3 py-3">Expiry date</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Sale #</th>
                <th className="px-3 py-3">Purchaser</th>
                <th className="px-3 py-3">Owner</th>
                <th className="px-3 py-3 text-right">Total</th>
                <th className="px-3 py-3 text-right">Redeemed</th>
                <th className="px-3 py-3 text-right">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map(v => (
                <tr key={v.id} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6] cursor-pointer transition-colors" onClick={() => setSelectedVoucher(v)} data-testid={`voucher-row-${v.id}`}>
                  <td className="px-4 py-3 font-mono text-[#556B2F] font-medium">{v.code}</td>
                  <td className="px-3 py-3 text-[#5C6656]">{v.issue_date}</td>
                  <td className="px-3 py-3 text-[#5C6656]">{v.expiry_date}</td>
                  <td className="px-3 py-3"><span className={`font-semibold capitalize ${statusColors[v.status] || 'text-[#5C6656]'}`}>{v.status === 'valid' ? 'Valid' : v.status === 'expired' ? 'Expired' : v.status === 'redeemed' ? 'Redeemed' : v.status === 'voided' ? 'Voided' : v.status === 'refunded' ? 'Refunded' : v.status}</span></td>
                  <td className="px-3 py-3 text-[#556B2F] font-medium">{v.sale_number}</td>
                  <td className="px-3 py-3 text-[#5C6656]">{v.purchaser_name || 'Walk-in'}</td>
                  <td className="px-3 py-3 text-[#5C6656]">{v.owner_name || 'Not claimed'}</td>
                  <td className="px-3 py-3 text-right font-medium text-[#1A2421]">{'\u00A3'}{(v.value||0).toFixed(2)}</td>
                  <td className="px-3 py-3 text-right text-[#5C6656]">{'\u00A3'}{(v.redeemed_amount||0).toFixed(2)}</td>
                  <td className="px-3 py-3 text-right font-medium text-[#1A2421]">{'\u00A3'}{(v.remaining||0).toFixed(2)}</td>
                </tr>
              ))}
              {vouchers.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-[#5C6656]">No gift cards sold yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CancellationsList() {
  const [cancellations, setCancellations] = useState([]);
  const [totalServices, setTotalServices] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const fetchCancellations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sales/cancellations`, { params: { date_from: dateFrom, date_to: dateTo }, withCredentials: true });
      setCancellations(data.items || []);
      setTotalServices(data.total_services || 0);
    } catch {}
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchCancellations(); }, [fetchCancellations]);

  const deleteCancellation = async (id, clientName, serviceCount) => {
    if (!window.confirm(`Delete cancellation for "${clientName}"${serviceCount > 1 ? ` (${serviceCount} services)` : ''}? This permanently removes it from all reports.`)) return;
    try {
      await axios.delete(`${API}/sales/cancellations/${id}`, { withCredentials: true });
      toast.success('Cancellation record deleted');
      fetchCancellations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const totalLostRevenue = cancellations.reduce((s, c) => s + (c.total_price || 0), 0);

  return (
    <div className="space-y-4" data-testid="cancellations-list">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Cancellations</h2>
          <p className="text-xs text-[#5C6656]">Cancelled appointments — delete accidental ones to clean up data</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 border border-[#E5E7E1] rounded-lg px-2 text-xs" />
          <span className="text-xs text-[#5C6656]">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 border border-[#E5E7E1] rounded-lg px-2 text-xs" />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E7E1] rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#E2725B]" style={{ fontFamily: 'Outfit' }}>{cancellations.length}</p>
          <p className="text-[10px] text-[#5C6656] uppercase tracking-wider font-bold mt-0.5">Client Cancellations</p>
        </div>
        <div className="bg-white border border-[#E5E7E1] rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#D4A373]" style={{ fontFamily: 'Outfit' }}>{totalServices}</p>
          <p className="text-[10px] text-[#5C6656] uppercase tracking-wider font-bold mt-0.5">Services Cancelled</p>
        </div>
        <div className="bg-white border border-[#E5E7E1] rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{'\u00A3'}{totalLostRevenue.toFixed(2)}</p>
          <p className="text-[10px] text-[#5C6656] uppercase tracking-wider font-bold mt-0.5">Lost Revenue</p>
        </div>
        <div className="bg-white border border-[#E5E7E1] rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#5C6656]" style={{ fontFamily: 'Outfit' }}>{cancellations.filter(c => c.cancel_reason && c.cancel_reason !== 'No reason provided').length}</p>
          <p className="text-[10px] text-[#5C6656] uppercase tracking-wider font-bold mt-0.5">With Reason</p>
        </div>
      </div>

      {loading ? <p className="text-center py-8 text-[#5C6656]">Loading...</p> : cancellations.length === 0 ? (
        <div className="bg-white border border-[#E5E7E1] rounded-xl p-12 text-center">
          <Ban size={32} className="mx-auto mb-2 text-[#E5E7E1]" />
          <p className="text-sm text-[#5C6656]">No cancellations in this period</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7E1] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F8F6]">
                <tr className="border-b border-[#E5E7E1]">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C6656]">Date</th>
                  <th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656] whitespace-nowrap">Cancelled at</th>
                  <th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Client</th>
                  <th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Services</th>
                  <th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Staff</th>
                  <th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Reason</th>
                  <th className="text-right px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Value</th>
                  <th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Source</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase text-[#5C6656] whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {cancellations.map(c => {
                  const cAt = c.cancelled_at ? new Date(c.cancelled_at) : null;
                  return (
                  <tr key={c.id} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6]" data-testid={`cancel-row-${c.id}`}>
                    <td className="px-4 py-3 text-[#1A2421] whitespace-nowrap">{c.date}<br/><span className="text-[10px] text-[#5C6656]">{c.start_time}</span></td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {cAt ? (
                        <div className="text-xs">
                          <div className="text-[#1A2421] font-medium">{format(cAt, 'd MMM yyyy')}</div>
                          <div className="text-[10px] text-[#5C6656]">{format(cAt, 'HH:mm')}{c.cancelled_by ? ` · ${c.cancelled_by}` : ''}</div>
                        </div>
                      ) : <span className="text-[10px] text-[#8A8E84] italic">Not tracked</span>}
                    </td>
                    <td className="px-3 py-3 font-medium text-[#1A2421] whitespace-nowrap">{c.client_name}</td>
                    <td className="px-3 py-3 text-[#5C6656] min-w-[180px]">
                      {(c.services || []).map((s, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate">{s.name}</span>
                          <span className="text-[10px] text-[#5C6656] flex-shrink-0">{'\u00A3'}{(s.price || 0).toFixed(0)}</span>
                        </div>
                      ))}
                      {(c.services || []).length > 1 && <span className="text-[10px] font-bold text-[#556B2F] mt-0.5 block">{c.services.length} services</span>}
                    </td>
                    <td className="px-3 py-3 text-[#5C6656] whitespace-nowrap">{c.staff_name}</td>
                    <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${c.cancel_reason && c.cancel_reason !== 'No reason provided' ? 'bg-[#D4A373]/10 text-[#D4A373]' : 'bg-[#E5E7E1] text-[#5C6656]'}`}>{c.cancel_reason || 'No reason'}</span></td>
                    <td className="px-3 py-3 text-right font-medium text-[#E2725B] whitespace-nowrap">{'\u00A3'}{(c.total_price || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">{c.source === 'online' ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2E86C1]/10 text-[#2E86C1] font-bold">Online</span> : <span className="text-[10px] text-[#5C6656]">Manual</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => deleteCancellation(c.id, c.client_name, (c.services || []).length)} className="text-xs text-[#E2725B] hover:text-[#C0392B] font-medium inline-flex items-center gap-1" data-testid={`delete-cancel-${c.id}`}>
                        <Trash2 size={12} /> Delete{(c.services || []).length > 1 ? ` (${c.services.length})` : ''}
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

